export interface LeaderboardBadge {
    ChallengeBadgeRuleId: number; ActivityName: string; ActivityKey: string;
    MetricCode: 'distance' | 'duration' | 'count'; MetricLabel: string; TierName: string;
    FrameKey: string; SymbolMaterialKey: string; Rank: number; Score: number;
    CountUnit: string; AveragePerMember: boolean;
}
export interface BadgeCollection {
    TotalCount?: number;
    IsFinal?: boolean; LeaderboardCount?: number; AchievementCount?: number;
    State: 'pending' | 'ready'; ScoreThroughDate?: string; PublishedDateTime?: string; Badges: LeaderboardBadge[];
    EarnedAwards?: EarnedAward[]; Progress?: AchievementProgress[];
}
export interface BadgeSummary {
    UserChallengeId?: number; ChallengeTeamId?: number; TotalCount: number; Badges: LeaderboardBadge[];
    LeaderboardCount?: number; AchievementCount?: number; EarnedAwards?: EarnedAward[];
}
export interface BadgeSummaries {
    IsFinal?: boolean;
    State: 'pending' | 'ready'; ScoreThroughDate?: string; PublishedDateTime?: string; Subjects: BadgeSummary[];
}
export interface EarnedAward {
    AwardId: number; Name: string; TierName: string; IconKey: string; FinalMedal: boolean;
    Backfilled: boolean; EarnedDateTime?: string; Rank?: number;
}

const achievementIcons = new Set(['team-player', 'team-founder', 'giving', 'recruiter', 'encourager', 'high-five', 'solid-streaker']);
export function achievementArtwork(award: EarnedAward): string {
    return achievementIcons.has(award.IconKey) ? 'fitness-achievement-' + award.IconKey : '';
}
export interface AchievementProgress {
    Key: string; Name: string; Value: number; NextThreshold?: number; Percent?: number;
    Unavailable: boolean; CompletedDays?: number; RequiredDays?: number;
}
export interface BadgeBoard {
    ChallengeBadgeRuleId: number; ActivityName: string; MetricLabel: string; IsTeam: boolean;
    FitnessActivityTypeId?: number; MetricCode?: 'distance' | 'duration' | 'count'; IsRanked?: boolean;
}
export interface BadgeBoardList { State: 'pending' | 'ready'; Boards: BadgeBoard[]; }
export interface BadgeStanding { UserChallengeId?: number; ChallengeTeamId?: number; Name: string; TeamSlug?: string; Badge: LeaderboardBadge; }
export interface BadgeBoardStandings { State: 'pending' | 'ready'; ScoreThroughDate?: string; Rows: BadgeStanding[]; }

export function orderedBadges(badges: LeaderboardBadge[]): LeaderboardBadge[] {
    const tiers = ['gold', 'silver', 'bronze', 'top10', 'top20'];
    const tier = (badge: LeaderboardBadge) => { const index = tiers.indexOf(badge.FrameKey); return index < 0 ? tiers.length : index; };
    return badges.slice().sort((a, b) => tier(a) - tier(b) || a.Rank - b.Rank ||
        a.ActivityName.localeCompare(b.ActivityName) || a.MetricCode.localeCompare(b.MetricCode) || a.ChallengeBadgeRuleId - b.ChallengeBadgeRuleId);
}

export function badgeScore(badge: LeaderboardBadge): string {
    const value = badge.MetricCode === 'distance' ? badge.Score / 1609.344 : badge.MetricCode === 'duration' ? badge.Score / 60 : badge.Score;
    const unit = badge.MetricCode === 'distance' ? 'mi' : badge.MetricCode === 'duration' ? 'min' : badge.CountUnit || 'count';
    return value.toLocaleString(undefined, { maximumFractionDigits: 2 }) + ' ' + unit + (badge.AveragePerMember ? ' per member' : '');
}

const rankArtwork = new Set(['running', 'biking', 'swimming', 'walking', 'hiking', 'othercardio', 'weightlifting',
    'elliptical', 'hiit', 'pushups', 'yoga', 'planks', 'handwashing', 'tennis']);
const baseArtwork = new Set([...rankArtwork, 'burpees', 'other', 'givingblood', 'socialdistancing', 'mindfulness', 'dryjanuary']);
export function badgeArtwork(badge: LeaderboardBadge): { frame: string; symbol: string; fallback: boolean } {
    const frame = ['gold', 'silver', 'bronze', 'top10', 'top20'].includes(badge.FrameKey) ? 'fitness-frame-' + badge.FrameKey : '';
    const material = ['gold', 'silver', 'bronze', 'mint'].includes(badge.SymbolMaterialKey) ? badge.SymbolMaterialKey : '';
    const key = baseArtwork.has(badge.ActivityKey) ? badge.ActivityKey : 'other';
    return { frame,
        symbol: rankArtwork.has(key) && material ? `fitness-rank-symbol-${key}-${material}` : 'fitness-icon-' + key,
        fallback: !rankArtwork.has(key) || !material };
}
